import React, { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { format, addDays, isAfter, isBefore, startOfDay } from 'date-fns';

interface TimeSlot {
  date: string;
  startTime: string;
  endTime: string;
}

interface AvailabilityCalendarProps {
  selectedSlots: TimeSlot[];
  onSlotsChange: (slots: TimeSlot[]) => void;
  timezone: string;
}

const TIME_OPTIONS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00', '19:30', '20:00'
];

export const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  selectedSlots,
  onSlotsChange,
  timezone
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');

  // Calculate date range (today to 2 weeks from today)
  const today = startOfDay(new Date());
  const maxDate = addDays(today, 14);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setStartTime('');
    setEndTime('');
  };

  const handleAddSlot = () => {
    if (!selectedDate || !startTime || !endTime) return;

    // Validate end time is after start time
    if (endTime <= startTime) {
      return;
    }

    const dateString = format(selectedDate, 'yyyy-MM-dd');
    
    // Check if slot already exists
    const slotExists = selectedSlots.some(slot => 
      slot.date === dateString && 
      slot.startTime === startTime && 
      slot.endTime === endTime
    );

    if (!slotExists) {
      const newSlot: TimeSlot = {
        date: dateString,
        startTime,
        endTime
      };
      
      onSlotsChange([...selectedSlots, newSlot]);
    }

    // Reset time selection but keep date
    setStartTime('');
    setEndTime('');
  };

  const handleRemoveSlot = (index: number) => {
    const updatedSlots = selectedSlots.filter((_, i) => i !== index);
    onSlotsChange(updatedSlots);
  };

  const isDateDisabled = (date: Date) => {
    return isBefore(date, today) || isAfter(date, maxDate);
  };

  const getDateSlots = (date: Date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    return selectedSlots.filter(slot => slot.date === dateString);
  };

  return (
    <div className="space-y-6">
      {/* Calendar */}
      <div>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          disabled={isDateDisabled}
          className="rounded-md border"
          modifiers={{
            hasSlots: (date) => getDateSlots(date).length > 0
          }}
          modifiersStyles={{
            hasSlots: { backgroundColor: 'hsl(var(--primary) / 0.1)' }
          }}
        />
      </div>

      {/* Time Selection */}
      {selectedDate && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">
                  Add availability for {format(selectedDate, 'EEEE, MMMM d')}
                </h4>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Start Time</label>
                  <select
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full p-2 border rounded-md bg-background"
                  >
                    <option value="">Select start time</option>
                    {TIME_OPTIONS.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">End Time</label>
                  <select
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full p-2 border rounded-md bg-background"
                    disabled={!startTime}
                  >
                    <option value="">Select end time</option>
                    {TIME_OPTIONS
                      .filter(time => !startTime || time > startTime)
                      .map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                  </select>
                </div>
              </div>
              
              <Button
                onClick={handleAddSlot}
                disabled={!startTime || !endTime}
                className="w-full"
                variant="outline"
              >
                Add Time Slot
              </Button>

              {/* Show existing slots for selected date */}
              {getDateSlots(selectedDate).length > 0 && (
                <div className="mt-4">
                  <h5 className="text-sm font-medium mb-2">Current availability:</h5>
                  <div className="space-y-2">
                    {getDateSlots(selectedDate).map((slot, index) => {
                      const globalIndex = selectedSlots.findIndex(s => 
                        s.date === slot.date && 
                        s.startTime === slot.startTime && 
                        s.endTime === slot.endTime
                      );
                      return (
                        <Badge key={index} variant="secondary" className="mr-2">
                          {slot.startTime} - {slot.endTime}
                          <button
                            onClick={() => handleRemoveSlot(globalIndex)}
                            className="ml-2 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
